import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left: Form */}
      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <img
                src="/brand/rcpl-logo.jpg?v=2"
                alt="Radiants Civiletric Pvt Ltd"
                className="h-12 w-auto object-contain"
              />
            </div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400 mb-2">
              Radiants Civiletric Pvt Ltd
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
              Volt<span className="text-[#002FA7]">Ledger</span>.
            </h1>
            <p className="mt-4 text-sm text-slate-500 leading-relaxed">
              Receivables, retentions &amp; reconciliation for civil &amp; electrical contracts. Sign in to continue.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                placeholder="you@company.com"
                className="mt-2 rounded-none border-slate-200 focus-visible:ring-1 focus-visible:ring-[#002FA7] focus-visible:border-[#002FA7]"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                placeholder="••••••••"
                className="mt-2 rounded-none border-slate-200 focus-visible:ring-1 focus-visible:ring-[#002FA7] focus-visible:border-[#002FA7]"
              />
            </div>

            {err && (
              <div className="text-xs text-red-600 border border-red-100 bg-red-50 px-3 py-2" data-testid="login-error">
                {err}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white rounded-none h-11 font-medium tracking-wide group"
            >
              {busy ? "Signing in…" : "Sign in"}
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-100 text-[11px] text-slate-400">
            Authorized personnel only. Contact admin for access.
          </div>
        </div>
      </div>

      {/* Right: Visual */}
      <div className="hidden lg:block relative bg-slate-900 overflow-hidden grain">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-90"
          style={{
            backgroundImage:
              "url('https://static.prod-images.emergentagent.com/jobs/4573c733-bb31-484e-ab56-4c5ec33086f7/images/b151f7a7108b410bc995c118260838db9e8141aba70354c548171e209e034355.png')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#002FA7]/30 via-transparent to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/70 mb-4">
            Financial Operations
          </div>
          <h2 className="font-display text-3xl font-bold tracking-tight">
            Track every rupee.<br />
            From invoice to retention release.
          </h2>
        </div>
      </div>
    </div>
  );
}
